import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Button, TextField, Modal } from '@mui/material';
import * as Styled from '../LoyaltyPageReport.styles';
import NetworkSelector from './NetworkSelector';
import { selectors as selectorsLogin } from '../../LoginPage/reducer';
import { selectors as loyaltySelectors } from '../../LoyaltyPage/reducer';
import * as actions from '../../LoyaltyPage/actions';

interface AdditionalBonusesProps {
  sources: { code: string; name: string }[];
}

const AdditionalBonuses = ({ sources }: AdditionalBonusesProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const dataUser = useSelector(selectorsLogin.userData);

  const creditBonusesLoading = useSelector(
    loyaltySelectors.creditBonusesLoading
  );
  const creditBonusesResult = useSelector(loyaltySelectors.creditBonusesResult);

  const [clientPhone, setClientPhone] = useState('');
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');

  const [selectedSource, setSelectedSource] = useState<{
    code: string;
    name: string;
  } | null>(null);
  const [isSourceOpen, setIsSourceOpen] = useState(false);

  const [isConfirmOpen, setIsConfirmOpen] = useState(false);

  // Валидация номера телефона: должен состоять из 11 цифр
  const isValidPhone = useMemo(() => {
    const cleaned = clientPhone.replace(/\D/g, '');
    return cleaned.length === 11 && /^\d{11}$/.test(cleaned);
  }, [clientPhone]);

  const isFormValid = useMemo(() => {
    const parsedAmount = Number(amount);
    return (
      isValidPhone &&
      !!reason.trim() &&
      !!selectedSource?.code &&
      !Number.isNaN(parsedAmount) &&
      parsedAmount > 0
    );
  }, [amount, isValidPhone, reason, selectedSource]);

  // Отслеживание успешного начисления и сброс формы
  useEffect(() => {
    if (creditBonusesResult && !creditBonusesLoading) {
      // Сброс формы после успешного начисления
      setClientPhone('');
      setAmount('');
      setReason('');
      setSelectedSource(null);
      setIsConfirmOpen(false);
    }
  }, [creditBonusesResult, creditBonusesLoading]);

  const handleSelectSource = (code: string, name: string) => {
    setSelectedSource({ code, name });
    setIsSourceOpen(false);
  };

  const handleOpenConfirm = () => {
    if (!isFormValid) return;
    setIsConfirmOpen(true);
  };

  const handleCloseConfirm = () => {
    if (creditBonusesLoading) return;
    setIsConfirmOpen(false);
  };

  const handleConfirm = () => {
    if (!isFormValid || !selectedSource) return;

    const cleanedPhone = clientPhone.replace(/\D/g, '');

    const payload = {
      user_phone: cleanedPhone,
      source_code: selectedSource.code || dataUser?.sourceCode || '',
      reason: reason.trim(),
      amount: Number(amount),
    };

    dispatch(actions.creditBonuses.request(payload));
    setIsConfirmOpen(false);
  };

  return (
    <Styled.AdditionalBonusContainer>
      <Styled.AdditionalBonusForm>
        <Styled.AdditionalBonusField>
          <span className="label">
            {t('LoyaltyPageReport.AdditionalBonuses.PHONE')}
          </span>
          <TextField
            size="small"
            variant="outlined"
            value={clientPhone}
            onChange={(e) => setClientPhone(e.target.value)}
            error={!!clientPhone && !isValidPhone}
            helperText={
              clientPhone && !isValidPhone
                ? t('LoyaltyPageReport.AdditionalBonuses.PHONE_ERROR')
                : ''
            }
            placeholder="77783784148"
          />
        </Styled.AdditionalBonusField>

        <Styled.AdditionalBonusField>
          <span className="label">
            {t('LoyaltyPageReport.AdditionalBonuses.SOURCE')}
          </span>
          <NetworkSelector
            isOpen={isSourceOpen}
            onClose={() => setIsSourceOpen(false)}
            onToggle={() => setIsSourceOpen((prev) => !prev)}
            selectedName={
              selectedSource?.name ||
              t('LoyaltyPageReport.AdditionalBonuses.SELECT_SOURCE')
            }
            sources={sources}
            onSelect={handleSelectSource}
            hideAll
          />
        </Styled.AdditionalBonusField>

        <Styled.AdditionalBonusField>
          <span className="label">
            {t('LoyaltyPageReport.AdditionalBonuses.BONUS')}
          </span>
          <TextField
            size="small"
            variant="outlined"
            type="number"
            inputProps={{ min: 1 }}
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
          />
        </Styled.AdditionalBonusField>

        <Styled.AdditionalBonusFieldReason>
          <span className="label">
            {t('LoyaltyPageReport.AdditionalBonuses.REASON')}
          </span>
          <TextField
            size="small"
            variant="outlined"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
        </Styled.AdditionalBonusFieldReason>
      </Styled.AdditionalBonusForm>

      <Styled.AdditionalBonusActions>
        <Button
          variant="contained"
          color="primary"
          onClick={handleOpenConfirm}
          disabled={!isFormValid || creditBonusesLoading}
        >
          {t('LoyaltyPageReport.AdditionalBonuses.CONFIRM')}
        </Button>
      </Styled.AdditionalBonusActions>

      {/* Диалог подтверждения начисления бонусов */}
      <Modal
        open={isConfirmOpen}
        onClose={handleCloseConfirm}
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <Styled.ConfirmationDeleteModal>
          <div className="title">
            {t('LoyaltyPageReport.AdditionalBonuses.CONFIRM_MODAL', {
              phone: clientPhone.trim(),
              amount,
              source: selectedSource?.name,
            })}
          </div>
          <div className="buttons-block">
            <button
              className="button-delete"
              type="button"
              onClick={handleConfirm}
              disabled={creditBonusesLoading}
            >
              {t('LoyaltyPageReport.AdditionalBonuses.YES')}
            </button>
            <button
              className="button-cancel"
              type="button"
              onClick={handleCloseConfirm}
              disabled={creditBonusesLoading}
            >
              {t('LoyaltyPageReport.AdditionalBonuses.NO')}
            </button>
          </div>
        </Styled.ConfirmationDeleteModal>
      </Modal>
    </Styled.AdditionalBonusContainer>
  );
};

export default AdditionalBonuses;
